/**
 * 한 해의 독서 궤적을 캔버스에 그린다 (1080×1350, 인스타 세로 규격).
 *
 * SVG 로 그려 이미지로 바꾸는 방법은 쓰지 않는다. 브라우저가 SVG 를
 * 이미지로 바꿀 때 외부 폰트를 불러오지 않아서, 한글이 기본 글꼴로
 * 깨지거나 빈칸으로 나온다. 폰트를 통째로 SVG 에 넣으면 수 MB 다.
 * 캔버스에 직접 그리면 페이지에 이미 로드된 웹폰트를 그대로 쓴다.
 */

/**
 * 두 규격으로 그린다. 별자리와 같은 이유다 —
 *
 *   feed   1080×1350 (4:5)  인스타 게시물
 *   story  1080×1920 (9:16) 인스타 스토리
 *
 * 스토리는 위아래 240px 쯤이 인스타 자기 UI 에 덮이므로 글자를 그
 * 안쪽으로 들여 놓는다. 좌표판 크기(520)는 두 규격에서 같게 둔다 —
 * 판이 커지면 같은 사람의 궤적이 규격에 따라 다른 모양으로 보인다.
 * 세로가 길어진 만큼은 책 목록에 준다(7줄 → 14줄).
 */
export const TRAIL_SIZES = {
  feed: {
    key: "feed", label: "4:5", w: 1080, h: 1350,
    capY: 140, nameY: 216, subY: 266, plotY: 322, footY: 1200, signY: 1280,
  },
  story: {
    key: "story", label: "9:16", w: 1080, h: 1920,
    capY: 300, nameY: 376, subY: 426, plotY: 500, footY: 1640, signY: 1700,
  },
};

export const CANVAS_W = TRAIL_SIZES.feed.w;
export const CANVAS_H = TRAIL_SIZES.feed.h;

const VOID = "#04050f", STAR = "#f5f0e8", GOLD = "#c9a84c", GLOW = "#7b6cd4";
const HAN = "'Noto Sans KR', sans-serif";
const LAT = "'Cormorant Garamond', serif";

/** 궤적을 그리려면 점이 이만큼은 있어야 한다. 두 점은 이야기가 아니다. */
export const MIN_POINTS = 3;

/** 같은 그림이 늘 같게 나오도록 자리를 고정한다 */
function rng(seed) {
  return () => {
    seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** "2026.09.04" → {y:2026, m:9} */
function parseDate(display) {
  const m = /^(\d{4})\.(\d{2})\./.exec(String(display ?? ""));
  return m ? { y: Number(m[1]), m: Number(m[2]) } : null;
}

/**
 * 그릴 수 있는 해와 그 데이터.
 * @returns {{year:number, points:Array, completed:number, missing:number}[]}
 */
export function collectYears(books, uid) {
  const byYear = new Map();
  for (const b of books || []) {
    const d = parseDate(b.completedBy?.[uid]);
    if (!d) continue;
    if (!byYear.has(d.y)) byYear.set(d.y, { year: d.y, points: [], completed: 0, missing: 0 });
    const bucket = byYear.get(d.y);
    bucket.completed++;
    const e = b.emotions?.[uid];
    if (e) bucket.points.push({ month: d.m, day: b.completedBy[uid], title: b.title, author: b.author, bright: e.bright, calm: e.calm });
    else bucket.missing++;
  }
  for (const v of byYear.values()) v.points.sort((a, b) => a.day.localeCompare(b.day));
  return [...byYear.values()].sort((a, b) => b.year - a.year);
}

/**
 * 웹폰트가 도착하기를 기다린다.
 *
 * document.fonts.load 는 글꼴 목록이 아니라 하나짜리 이름을 받는다.
 * "'Noto Sans KR', sans-serif" 를 넘기면 거절된다.
 */
export async function waitForFonts() {
  if (!document.fonts) return;
  const specs = [`700 58px "Noto Sans KR"`, `400 26px "Noto Sans KR"`, `300 58px "Cormorant Garamond"`];
  await Promise.all(
    specs.map((s) => document.fonts.load(s).catch(() => {}))
  );
  await document.fonts.ready.catch(() => {});
}

function roundedText(ctx, text, x, y, { font, fill, align = "left", alpha = 1 }) {
  ctx.save();
  ctx.font = font;
  ctx.fillStyle = fill;
  ctx.textAlign = align;
  ctx.globalAlpha = alpha;
  ctx.fillText(text, x, y);
  ctx.restore();
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {{year, name, points, completed, missing}} data
 */
export function drawYearTrail(canvas, { year, name, points, completed, missing, size = "feed" }) {
  const L = TRAIL_SIZES[size] ?? TRAIL_SIZES.feed;
  const CW = L.w, CH = L.h;
  const ctx = canvas.getContext("2d");
  canvas.width = CW;
  canvas.height = CH;

  // ── 배경 ──
  const bg = ctx.createRadialGradient(CW / 2, CH * 0.34, 0, CW / 2, CH * 0.34, CW * 0.9);
  bg.addColorStop(0, "#12102f");
  bg.addColorStop(1, VOID);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, CW, CH);

  const r = rng(901);
  const dust = Math.round(70 * (CH / TRAIL_SIZES.feed.h));
  for (let i = 0; i < dust; i++) {
    ctx.globalAlpha = r() * 0.45 + 0.1;
    ctx.fillStyle = r() > 0.86 ? "#c8b6ff" : STAR;
    ctx.beginPath();
    ctx.arc(r() * CW, r() * CH, r() * 2 + 0.5, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // ── 제목 ──
  ctx.textBaseline = "alphabetic";
  roundedText(ctx, `${year} TRAIL`, CW / 2, L.capY, { font: `300 30px ${LAT}`, fill: GOLD, align: "center", alpha: 0.8 });
  roundedText(ctx, `${name}님의 ${year} 궤적`, CW / 2, L.nameY, { font: `700 54px ${HAN}`, fill: STAR, align: "center" });
  roundedText(ctx, `올해 ${completed}권을 읽었습니다`, CW / 2, L.subY, { font: `400 30px ${HAN}`, fill: STAR, align: "center", alpha: 0.45 });

  // ── 좌표판 ──
  const S = 520, PX = (CW - S) / 2, PY = L.plotY;
  ctx.strokeStyle = STAR;
  ctx.globalAlpha = 0.14;
  ctx.lineWidth = 1.4;
  ctx.strokeRect(PX, PY, S, S);
  ctx.globalAlpha = 0.08;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(PX, PY + S / 2); ctx.lineTo(PX + S, PY + S / 2);
  ctx.moveTo(PX + S / 2, PY); ctx.lineTo(PX + S / 2, PY + S);
  ctx.stroke();
  ctx.globalAlpha = 1;

  /* 축 이름. 세로축(차분↔격렬)을 아래 모서리에 나란히 찍고 있었다 —
     가로축 이름이 두 벌로 읽혀 좌표 전체가 뒤집혀 보였다. 위·아래로 옮긴다.

     소설과 비소설은 묻는 것이 다르다(EmotionCoordinate 참고).
       소설   밝음↔어두움 / 차분↔격렬
       비소설 가벼움↔무거움 / 실용적↔사색적
     이 그림은 둘을 한 판에 그리므로 두 뜻을 함께 적는다. 한쪽 이름만 쓰면
     다른 쪽 좌표에 남의 이름표가 붙는다. */
  const ax = { font: `400 26px ${HAN}`, fill: STAR, alpha: 0.45 };
  /* 가로축 이름은 상자 좌우 바깥, 세로 가운데에 붙인다. 셋을 위쪽 줄에
     몰아두니 어느 것이 가로축인지 읽히지 않았다. 좌우 여백이 각각 280px 라
     26px 글자가 들어간다. 세로축은 위·아래 가운데. */
  const midY = PY + S / 2 + 9;
  roundedText(ctx, "밝고 가벼움", PX - 16, midY, { ...ax, align: "right" });
  roundedText(ctx, "어둡고 무거움", PX + S + 16, midY, ax);
  /* 세로축 이름은 상자 '안쪽' 위·아래에 둔다. 바깥 위에 두었더니 피드(4:5)
     에서 바로 위 '올해 N권을 읽었습니다' 와 겹쳤다 — 그 규격은 제목 세 줄이
     상자에 바짝 붙어 있어 넣을 틈이 없다. 안쪽은 두 규격 모두 비어 있다. */
  roundedText(ctx, "차분 · 실용", PX + S / 2, PY + 34, { ...ax, align: "center" });
  roundedText(ctx, "격렬 · 사색", PX + S / 2, PY + S - 18, { ...ax, align: "center" });

  const px = (b) => PX + (b / 100) * S;
  const py = (c) => PY + (c / 100) * S;

  // ── 궤적 ──
  if (points.length > 1) {
    const grad = ctx.createLinearGradient(PX, PY, PX + S, PY + S);
    grad.addColorStop(0, GLOW);
    grad.addColorStop(1, GOLD);
    ctx.strokeStyle = grad;
    ctx.lineWidth = 3;
    ctx.lineJoin = "round";
    ctx.globalAlpha = 0.75;
    ctx.beginPath();
    points.forEach((p, i) => (i ? ctx.lineTo(px(p.bright), py(p.calm)) : ctx.moveTo(px(p.bright), py(p.calm))));
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  // ── 점 · 번호 ──
  // 점이 가까우면 번호가 겹친다. 놓인 자리를 기억해 반대편으로 피한다.
  const placed = [];
  points.forEach((p, i) => {
    const x = px(p.bright), y = py(p.calm);
    const edge = i === 0 || i === points.length - 1;

    const glow = ctx.createRadialGradient(x, y, 0, x, y, edge ? 46 : 34);
    glow.addColorStop(0, "rgba(201,168,76,.5)");
    glow.addColorStop(1, "rgba(201,168,76,0)");
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(x, y, edge ? 46 : 34, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = STAR;
    ctx.beginPath();
    ctx.arc(x, y, edge ? 11 : 8, 0, Math.PI * 2);
    ctx.fill();

    let lx = x, ly = y < PY + 44 ? y + 42 : y - 24;
    const clash = (a, b) => placed.some((q) => Math.abs(q.x - a) < 34 && Math.abs(q.y - b) < 30);
    if (clash(lx, ly)) ly = ly > y ? y - 24 : y + 42;
    if (clash(lx, ly)) lx = x + 30;
    placed.push({ x: lx, y: ly });

    roundedText(ctx, String(i + 1), lx, ly, { font: `400 26px ${LAT}`, fill: GOLD, align: "center", alpha: 0.9 });
  });

  // ── 목록 ──
  //
  // 16권이면 목록이 카드를 넘고 MUROO 서명까지 덮었다. 제목과 저자도
  // 서로 파고들었다 — 긴 제목("…현대문학상 수상소설집")과 여러 명의
  // 저자가 각각 상대 칸으로 넘어갔다.
  //
  // 들어갈 만큼만 적고 나머지는 '외 N권' 으로 접는다. 칸을 정해두고
  // 넘치면 잘라 '…' 를 붙인다.

  /** 글자가 칸을 넘으면 잘라내고 '…' 를 붙인다 */
  const fit = (text, font, maxW) => {
    ctx.font = font;
    let t = String(text ?? "");
    if (!t || ctx.measureText(t).width <= maxW) return t;
    while (t.length > 1 && ctx.measureText(t + "…").width > maxW) t = t.slice(0, -1);
    return t + "…";
  };

  const listY = PY + S + 88;
  const FOOT = L.footY;             // 서명이 시작되기 전까지
  const LINE = 36;
  const TITLE_FONT = `400 26px ${HAN}`;
  const AUTHOR_FONT = `400 24px ${HAN}`;
  const NUM_W = 34;
  const AUTHOR_W = S * 0.34;
  const TITLE_W = S - NUM_W - AUTHOR_W - 16;

  const room = Math.max(1, Math.floor((FOOT - listY) / LINE));
  const fitsAll = points.length <= room;
  const shown = fitsAll ? points : points.slice(0, room - 1);

  shown.forEach((p, i) => {
    const y = listY + i * LINE;
    roundedText(ctx, String(i + 1), PX, y, { font: `400 24px ${LAT}`, fill: GOLD, alpha: 0.7 });
    roundedText(ctx, fit(p.title, TITLE_FONT, TITLE_W), PX + NUM_W, y,
      { font: TITLE_FONT, fill: STAR, alpha: 0.82 });
    roundedText(ctx, fit(p.author || "", AUTHOR_FONT, AUTHOR_W), PX + S, y,
      { font: AUTHOR_FONT, fill: STAR, align: "right", alpha: 0.38 });
  });

  let tailY = listY + shown.length * LINE;
  if (!fitsAll) {
    roundedText(ctx, `외 ${points.length - shown.length}권`, PX + NUM_W, tailY,
      { font: AUTHOR_FONT, fill: STAR, alpha: 0.45 });
    tailY += LINE;
  }

  // 선에서 빠진 책도 밝힌다 — 9권 완독인데 점이 7개면 숫자가 안 맞아 이상하다
  if (missing > 0 && tailY + 24 < FOOT + 40) {
    roundedText(ctx, `좌표를 찍지 않은 ${missing}권은 선에서 빠졌습니다`, CW / 2,
      tailY + 20, { font: `400 24px ${HAN}`, fill: STAR, align: "center", alpha: 0.3 });
  }

  roundedText(ctx, "MUROO", CW / 2, L.signY, { font: `300 30px ${LAT}`, fill: STAR, align: "center", alpha: 0.4 });
  roundedText(ctx, "muroo.kr", CW / 2, L.signY + 36, { font: `300 24px ${LAT}`, fill: GOLD, align: "center", alpha: 0.5 });
}
